import type { OkraClientOptions, ProcessingCapabilities } from './types';
import { OkraClient } from './client';
import { workflowPreset, type WorkflowPresetName } from './workflows';
import { Data, Effect } from 'effect';

export type ExtractionPhase = 'ocr' | 'enhance' | 'metadata' | 'verify';

export interface OkraProvider {
  name: string;
  supportedPhases: ExtractionPhase[];
}

export interface OkraMiddleware {
  name: string;
  config: Record<string, unknown>;
}

export interface CreateOkraOptions extends OkraClientOptions {
  providers?: Record<string, OkraProvider>;
  extraction?: Partial<Record<ExtractionPhase, string>>;
  middleware?: OkraMiddleware[];
  /** Legacy default BYOK keys (prefer `workflow.vendorKeys`). */
  vendorKeys?: Record<string, string>;
  /** General workflow defaults for all uploads from this client. */
  workflow?: {
    preset?: WorkflowPresetName;
    capabilities?: ProcessingCapabilities;
    vendorKeys?: Record<string, string>;
  };
}

export type ProviderConfigurationOperation = 'validate' | 'create_client';
export type ProviderConfigurationFailureReason = 'provider_not_registered' | 'client_configuration';

/** Safe native failure: API keys, vendor keys, middleware config, and options are not retained. */
export class ProviderConfigurationError extends Data.TaggedError('ProviderConfigurationError')<{
  readonly operation: ProviderConfigurationOperation;
  readonly reason: ProviderConfigurationFailureReason;
  readonly message: string;
  readonly phase?: ExtractionPhase;
  readonly provider?: string;
  readonly available: readonly string[];
}> {}

const providerConfigurationErrorCauses = new WeakMap<ProviderConfigurationError, unknown>();

function providerConfigurationFailure(
  fields: ConstructorParameters<typeof ProviderConfigurationError>[0],
  cause: unknown,
): ProviderConfigurationError {
  const error = new ProviderConfigurationError(fields);
  providerConfigurationErrorCauses.set(error, cause);
  return error;
}

/** Original compatibility cause without exposing client credentials on the tagged failure. */
export function providerConfigurationErrorCause(error: ProviderConfigurationError): unknown {
  return providerConfigurationErrorCauses.get(error) ?? error;
}

export function restoreProviderConfigurationError<A>(
  program: Effect.Effect<A, ProviderConfigurationError>,
): Effect.Effect<A, unknown> {
  return program.pipe(Effect.catch((error) => Effect.fail(providerConfigurationErrorCause(error))));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function deepMerge(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    const existing = merged[key];
    if (isPlainObject(existing) && isPlainObject(value)) {
      merged[key] = deepMerge(existing, value);
    } else {
      merged[key] = value;
    }
  }
  return merged;
}

/**
 * Factory function — AI SDK-style provider abstraction.
 *
 * ```ts
 * import { createOkra } from 'okrapdf';
 *
 * const okra = createOkra({
 *   apiKey: 'okra_...',
 *   providers: { azureDocAI },
 *   extraction: { ocr: 'azureDocAI' },
 * });
 * ```
 */
export function createOkraEffect(
  options: CreateOkraOptions,
): Effect.Effect<OkraClient, ProviderConfigurationError> {
  return Effect.gen(function* () {
    if (options.extraction && options.providers) {
      for (const [phase, providerName] of Object.entries(options.extraction)) {
        if (!options.providers[providerName]) {
          const available = Object.keys(options.providers);
          const message =
            `Extraction phase '${phase}' references provider '${providerName}' which is not registered. ` +
            `Available: ${available.join(', ')}`;
          return yield* Effect.fail(
            providerConfigurationFailure(
              {
                operation: 'validate',
                reason: 'provider_not_registered',
                message,
                phase: phase as ExtractionPhase,
                provider: providerName,
                available,
              },
              new Error(message),
            ),
          );
        }
      }
    }

    return yield* Effect.try({
      try: () => createConfiguredOkraClient(options),
      catch: (cause) =>
        providerConfigurationFailure(
          {
            operation: 'create_client',
            reason: 'client_configuration',
            message: 'Okra client configuration is invalid',
            available: Object.keys(options.providers ?? {}),
          },
          cause,
        ),
    });
  });
}

function createConfiguredOkraClient(options: CreateOkraOptions): OkraClient {
  let capabilities: Record<string, unknown> = options.workflow?.preset
    ? (workflowPreset(options.workflow.preset) as Record<string, unknown>)
    : {};
  if (options.extraction) {
    const phases =
      capabilities.phases && isPlainObject(capabilities.phases)
        ? { ...(capabilities.phases as Record<string, unknown>) }
        : {};
    for (const [phase, providerName] of Object.entries(options.extraction)) {
      phases[phase] = {
        vendor: providerName,
        enabled: true,
      };
    }
    capabilities.phases = phases;
  }

  if (options.middleware) {
    capabilities.middleware = options.middleware.map((m) => ({
      name: m.name,
      ...m.config,
    }));
  }
  if (options.workflow?.capabilities) {
    capabilities = deepMerge(
      capabilities,
      options.workflow.capabilities as Record<string, unknown>,
    );
  }

  const client = new OkraClient({
    baseUrl: options.baseUrl,
    apiKey: options.apiKey,
    sharedSecret: options.sharedSecret,
    fetch: options.fetch,
  });

  const defaultVendorKeys = options.workflow?.vendorKeys ?? options.vendorKeys;
  const hasDefaults = Object.keys(capabilities).length > 0 || defaultVendorKeys;
  if (hasDefaults) {
    const upload = client.upload.bind(client);
    client.upload = ((
      input: Parameters<OkraClient['upload']>[0],
      uploadOptions: Parameters<OkraClient['upload']>[1] = {},
    ) =>
      upload(input, {
        ...uploadOptions,
        capabilities: uploadOptions.capabilities
          ? deepMerge(capabilities, uploadOptions.capabilities)
          : Object.keys(capabilities).length > 0
            ? capabilities
            : uploadOptions.capabilities,
        vendorKeys: uploadOptions.vendorKeys
          ? { ...defaultVendorKeys, ...uploadOptions.vendorKeys }
          : defaultVendorKeys,
      })) as OkraClient['upload'];
  }

  return client;
}

/** Synchronous compatibility adapter for the original provider factory. */
export function createOkra(options: CreateOkraOptions): OkraClient {
  return Effect.runSync(restoreProviderConfigurationError(createOkraEffect(options)));
}

// ─── Built-in middleware constructors ──────────────────────────────────────

export function withCache(opts: { by: 'pdf-hash' | 'content-hash' }): OkraMiddleware {
  return { name: 'cache', config: { strategy: opts.by } };
}

export function withQualityScore(opts: { threshold: number }): OkraMiddleware {
  return { name: 'quality-score', config: { threshold: opts.threshold } };
}

export function withSecret(namespace: string, opts?: { required?: boolean }): OkraMiddleware {
  return {
    name: 'secret',
    config: { namespace, required: opts?.required !== false },
  };
}
